import {
  Box,
  Button,
  ButtonBase,
  Dialog,
  Grid,
  IconButton,
  Typography,
} from '@mui/material';
import { styled } from '@mui/material/styles';

export const ContainerBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row-reverse',
}));

export const OpenDialogButton = styled(Button)(({ theme }) => ({
  width: 210,
  height: 32,
  border: `1.5px solid ${theme.palette.secondary.main}`,
  borderRadius: 10,
  color: theme.palette.secondary.main,
  fontWeight: 600,
  backgroundColor: '#FAFCFF',
  display: 'flex',
  gap: 4,
  textTransform: 'lowercase',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
}));

export const StyledDialog = styled(Dialog)(({ theme }) => ({
  '& .MuiPaper-root': {
    borderRadius: 30,
    backgroundColor: theme.palette.common.white,
    padding: '48px',
    minHeight: 'auto',
    [theme.breakpoints.down('xl')]: {
      minWidth: 780,
      '& .css-biqaug': {
        margin: '48px 60px',
      },
    },
    [theme.breakpoints.up('xl')]: {
      minWidth: 920,
    },
  },
  '& .MuiDialog-paperScrollPaper': {
    '&::-webkit-scrollbar': {
      width: 10,
    },
    '&::-webkit-scrollbar-thumb': {
      backgroundColor: theme.palette.secondary.main,
      borderRadius: 10,
      '&:hover': {
        backgroundColor: theme.palette.secondary.dark,
      },
    },
    '&::-webkit-scrollbar-track': {
      margin: '60px 0',
    },
  },
}));

export const AttachmentsBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
}));

export const DialogTitle = styled(Typography)(({ theme }) => ({
  font: '700 21px Montserrat',
  textTransform: 'capitalize',
  color: '#595959',
  marginBottom: 24,
  display: 'flex',
  alignItems: 'center',
  gap: '10px',
  '& span': { color: theme.palette.secondary.main },
}));

export const FileList = styled('ul')(({ theme }) => ({
  display: 'flex',
  flexDirection: 'column',
  width: '100%',
  paddingLeft: 0,
  padding: 'none',
  listStyleType: 'none',
  marginTop: 0,
  '& span': { color: theme.palette.secondary.main },
}));

export const List = styled('li')(({ theme }) => ({
  borderBottom: '1px solid #ccc',
  padding: '12px 4px',
  font: '400 16px Montserrat',
  width: '100%',
  color: '#595959',
  display: 'flex',
  alignItems: 'center',
  justifyContent: 'space-between',
  '& div': {
    display: 'flex',
    alignItems: 'center',
    gap: '6px',
  },
  '&:first-of-type': {
    borderTop: '1px solid #ccc',
  },
  '& span': {
    fontSize: 20,
    fontWeight: 400,
    color: theme.palette.secondary.main,
  },
  '& button:disabled': {
    '& span': {
      color: '#ccc',
    },
  },
}));

export const FileContainer = styled(Grid)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'space-between',
  alignItems: 'center',
  width: '100%',
  borderBottom: '1px solid #ccc',
  padding: '8px 0 8px 4px',
}));

export const FileNameTypography = styled(Typography)(({ theme }) => ({
  minWidth: '300px',
  display: 'flex',
  gap: '6px',
  alignContent: 'center',
  color: '#595959',
  font: '400 16px Montserrat',
  '& span': {
    color: theme.palette.secondary.main,
    fontSize: 20,
  },
}));

export const DateTypography = styled(Typography)(() => ({
  color: '#717171',
  fontSize: '14px',
  minWidth: '80px',
  font: '400 12px Montserrat',
}));

export const StyledDownload = styled(ButtonBase)(({ theme }) => ({
  span: {
    fontSize: 20,
    color: theme.palette.secondary.main,
    borderRadius: '50%',
    padding: '4px',
  },
}));

export const StyledIcon = styled(ButtonBase)(({ theme }) => ({
  span: {
    color: theme.palette.secondary.main,
    fontSize: 20,
    padding: '4px',
    borderRadius: '50%',
  },
}));

export const TopBox = styled(Box)(() => ({
  display: 'flex',
  justifyContent: 'space-between',
}));

export const CloseButton = styled(IconButton)(({ theme }) => ({
  margin: '-28px -28px 0 0',
  height: 'fit-content',
  '& span': {
    fontSize: '24px',
    fontWeight: '600',
    color: theme.palette.secondary.main,
  },
}));

export const UploadBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'column',
  justifyContent: 'center',
  alignItems: 'center',
  padding: '24px',
  borderRadius: '30px',
  border: '0.5px solid #8B8C8E',
}));

type AttachFileButtonProps = {
  component: string;
};

export const AttachFileButon = styled(Button)<AttachFileButtonProps>(
  ({ component, theme }) => ({
    component,
    width: '220px',
    margin: '10px',
    color: '#FFFFFF',
    backgroundColor: theme.palette.secondary.main,
    '&:hover': {
      backgroundColor: theme.palette.secondary.main,
      filter: 'brightness(85%)',
    },
    [theme.breakpoints.down('xl')]: {
      width: '170px',
    },
    [theme.breakpoints.up('xl')]: {
      width: '220px',
    },
    '&:disabled': {
      filter: 'opacity(50%)',
    },
  })
);

export const InputBox = styled(Box)(() => ({
  display: 'flex',
  width: '100%',
  alignItems: 'center',
  marginTop: '24px',
}));

export const SubmitBox = styled(Box)(() => ({
  display: 'flex',
  flexDirection: 'row',
  justifyContent: 'center',
  gap: '24px',
  marginTop: '24px',
  width: '100%',
}));

export const CancelButton = styled(Button)(({ theme }) => ({
  backgroundColor: 'transparent',
  fontWeight: '700',
  color: theme.palette.secondary.main,
  border: `1px solid ${theme.palette.secondary.main}`,
  borderRadius: '30px',
  '&:hover': {
    backgroundColor: theme.palette.secondary.main,
  },
  [theme.breakpoints.down('xl')]: {
    width: '150px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
  },
}));

export const SubmitButton = styled(Button)(({ theme }) => ({
  width: '220px',
  backgroundColor: theme.palette.secondary.main,
  '&:hover': {
    filter: 'brightness(85%)',
  },
  [theme.breakpoints.down('xl')]: {
    width: '150px',
  },
  [theme.breakpoints.up('xl')]: {
    width: '220px',
  },
}));
